/**
 * Trace Service - API Implementation
 * Servicio de trazabilidad usando la API del servidor
 * @module services/traceServiceApi
 */

import { apiClient } from './apiClient'
import { TraceResult } from '@/types/fibers'

interface ApiTraceResult {
  success: boolean
  fiberId: string
  originId: string
  destinationId: string
  path: Array<{
    nodeId: string
    nodeName: string
    nodeType: string
    nodeRefCode?: string
    cableId: string
    cableCodigo?: string
    cableNombre?: string
    distanceKm: number
    hasSplice: boolean
    spliceVerificado?: boolean
    lat: number
    lng: number
  }>
  totalDistanceKm: number
  totalAttenuation: number
  connectorsCount: number
  splicingPoints: string[]
  hops: number
  tracedAt: string
  error?: string
  warnings?: string[]
}

function mapApiResultToTraceResult(apiResult: ApiTraceResult): TraceResult {
  return {
    success: apiResult.success,
    fiberId: apiResult.fiberId,
    originId: apiResult.originId,
    path: apiResult.path.map(segment => ({
      fiberId: segment.cableId,
      cableId: segment.cableId,
      nodeId: segment.nodeId,
      nodeRefCode: segment.nodeRefCode,
      cableCodigo: segment.cableCodigo,
      cableNombre: segment.cableNombre,
      nodeType: segment.nodeType.toLowerCase() as 'cable' | 'cierre' | 'nap' | 'cliente' | 'pop',
      description: segment.nodeName,
      distanceKm: segment.distanceKm,
      distance: segment.distanceKm * 1000,
      hasSplice: segment.hasSplice,
      spliceVerificado: segment.spliceVerificado,
      attenuation: 0,
      coordinates: { lat: segment.lat, lng: segment.lng },
    })),
    totalDistanceKm: apiResult.totalDistanceKm,
    totalAttenuation: apiResult.totalAttenuation,
    connectorsCount: apiResult.connectorsCount,
    splicingPoints: apiResult.splicingPoints,
    hops: apiResult.hops,
    tracedAt: new Date(apiResult.tracedAt),
    error: apiResult.error,
    warnings: apiResult.warnings || [],
  }
}

function createEmptyTraceResult(error: string, originId = '', fiberId = ''): TraceResult {
  return {
    success: false,
    fiberId,
    originId,
    path: [],
    totalDistanceKm: 0,
    totalAttenuation: 0,
    connectorsCount: 0,
    splicingPoints: [],
    hops: 0,
    tracedAt: new Date(),
    error,
    warnings: [],
  }
}

export const traceServiceApi = {
  /**
   * Trazar fibra desde un nodo hasta el POP/OLT
   */
  async traceFromNode(nodeId: string, fiberId?: string): Promise<TraceResult> {
    try {
      const result = await apiClient.post<ApiTraceResult>('/api/trace/fiber', {
        startNodeId: nodeId,
        fiberId: fiberId || '',
      })
      return mapApiResultToTraceResult(result)
    } catch (error) {
      console.error('Error tracing from node:', error)
      return createEmptyTraceResult(
        error instanceof Error ? error.message : 'Error desconocido',
        nodeId,
        fiberId
      )
    }
  },

  /**
   * Trazar fibra desde un cliente hasta el POP/OLT
   */
  async traceFromClient(clientId: string): Promise<TraceResult> {
    try {
      const result = await apiClient.get<ApiTraceResult>(`/api/trace/client/${clientId}`)
      return mapApiResultToTraceResult(result)
    } catch (error) {
      console.error('Error tracing from client:', error)
      return createEmptyTraceResult(
        error instanceof Error ? error.message : 'Error desconocido',
        clientId
      )
    }
  },

  /**
   * Obtener grafo de la red para visualización
   */
  async getNetworkGraph(): Promise<{
    nodes: Array<{ id: string; type: string; name: string; lat: number; lng: number }>
    edges: Array<{ id: string; source: string; target: string; type: string; distance: number }>
  }> {
    try {
      return await apiClient.get('/api/trace/network-graph')
    } catch (error) {
      console.error('Error fetching network graph:', error)
      return { nodes: [], edges: [] }
    }
  },
}
